/**
 * Render markdown text with inline entity reference chips.
 *
 * Pipeline: render the full markdown first so formatting markers like
 * **bold** stay paired, then walk the resulting DOM text nodes and
 * substitute reference labels with entity chips.
 */

import { renderMarkdown } from './markdown.js';
import { createEntityChip } from './entity-chip.js';

/**
 * @param {string} content
 * @param {Array} references
 * @returns {HTMLElement}
 */
export function createTextWithReferences(content, references) {
  const container = document.createElement('div');
  container.className = 'ai-text-with-references';

  const html = renderMarkdown(content);
  if (!html) return container;

  container.innerHTML = html;

  const inlineRefs = (references || [])
    .map((ref, i) => ({ ref, refIndex: i }))
    .filter(({ ref }) => ref.label && ref.inline !== false)
    .sort((a, b) => b.ref.label.length - a.ref.label.length);

  for (const { ref } of inlineRefs) {
    substituteInTextNodes(container, ref);
  }

  return container;
}

/**
 * Walk all text nodes under `root` and replace occurrences of `ref.label`
 * with entity chip elements (case-insensitive, longest-first).
 */
function substituteInTextNodes(root, ref) {
  const needle = ref.label.toLowerCase();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const matches = [];

  let node;
  while ((node = walker.nextNode())) {
    if (node.textContent.toLowerCase().includes(needle)) {
      matches.push(node);
    }
  }

  for (const textNode of matches) {
    const text = textNode.textContent;
    const haystack = text.toLowerCase();
    const fragments = [];
    let cursor = 0;

    while (cursor < text.length) {
      const idx = haystack.indexOf(needle, cursor);
      if (idx === -1) {
        fragments.push(document.createTextNode(text.slice(cursor)));
        break;
      }
      if (idx > cursor) {
        fragments.push(document.createTextNode(text.slice(cursor, idx)));
      }
      const matchedText = text.slice(idx, idx + ref.label.length);
      fragments.push(createEntityChip(ref, 'inline', matchedText));
      cursor = idx + ref.label.length;
    }

    if (fragments.length > 1 || (fragments.length === 1 && fragments[0] !== textNode)) {
      const parent = textNode.parentNode;
      for (const frag of fragments) {
        parent.insertBefore(frag, textNode);
      }
      parent.removeChild(textNode);
    }
  }
}
